import React, { useState } from 'react';
import {
  GetCampaignMessagingResponse,
  UpdateCampaignEmailBody,
  UpdateCampaignEmailResponse,
  CampaignErrorResponse,
} from '@dreamai/types';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../../../api';
import WysiwygEditor from '../../../common/WysiwygEditor';
import ErrorState from '../../../common/ErrorState';

type EmailType = GetCampaignMessagingResponse['sequences'][0]['emails'][0];

interface CampaignEditMessagingEmailProps {
  email: EmailType;
  campaignId: string;
  isFirstEmail: boolean;
  onCancel: () => void;
  onSave: () => void;
}

// Helper function to convert plain text with newlines to HTML for WYSIWYG editor
const prepareTemplateForEditor = (template: string | null): string => {
  if (!template) return '';

  // If template already contains HTML tags, return as-is
  if (/<\/?[a-z][\s\S]*>/i.test(template)) {
    return template;
  }

  // Split by double newlines for paragraphs
  const paragraphs = template.split('\n\n').filter((p) => p.trim());

  // Convert each paragraph, replacing single newlines with <br> and adding inline styles
  // Use same inline styles as email sending for consistency (12px bottom margin)
  const htmlParagraphs = paragraphs.map((paragraph) => {
    const withBreaks = paragraph.replace(/\n/g, '<br />');
    return `<p style="margin: 0 0 12px 0; padding: 0;">${withBreaks}</p>`;
  });

  return htmlParagraphs.join('');
};

// Clean and normalize HTML for email sending (industry standard)
// Removes empty tags and adds inline styles for consistent rendering across email clients
const cleanHtmlForEmail = (html: string): string => {
  if (!html) return '';

  // Remove empty paragraph tags (creates unwanted whitespace)
  let cleaned = html.replace(/<p>\s*<\/p>/gi, '');

  // Remove empty div and span tags
  cleaned = cleaned.replace(/<div>\s*<\/div>/gi, '');
  cleaned = cleaned.replace(/<span>\s*<\/span>/gi, '');

  // Add inline styles to paragraphs for consistent spacing (email industry standard)
  // This ensures emails look the same in Gmail, Outlook, Apple Mail, etc.
  cleaned = cleaned.replace(
    /<p>/gi,
    '<p style="margin: 0 0 12px 0; padding: 0;">',
  );

  // Ensure lists have proper spacing
  cleaned = cleaned.replace(
    /<ul>/gi,
    '<ul style="margin: 0 0 12px 0; padding: 0 0 0 20px;">',
  );

  cleaned = cleaned.replace(
    /<ol>/gi,
    '<ol style="margin: 0 0 12px 0; padding: 0 0 0 20px;">',
  );

  // Add spacing to list items
  cleaned = cleaned.replace(/<li>/gi, '<li style="margin: 0 0 4px 0;">');

  // Remove excessive whitespace
  cleaned = cleaned.replace(/\n\s*\n/g, '\n');

  return cleaned.trim();
};

const CampaignEditMessagingEmail: React.FC<CampaignEditMessagingEmailProps> = ({
  email,
  campaignId,
  isFirstEmail,
  onCancel,
  onSave,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [sendAsNewThread, setSendAsNewThread] = useState(
    email.isNewThread || false,
  );
  const [sendDelay, setSendDelay] = useState(email.delay);
  const [subject, setSubject] = useState(email.subjectValue || '');
  const [message, setMessage] = useState(
    prepareTemplateForEditor(email.template),
  );
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);

    try {
      const token = await getAccessTokenSilently();
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const requestBody: UpdateCampaignEmailBody = {
        subject,
        template: cleanHtmlForEmail(message),
        delay: sendDelay,
        sendAsNewThread,
      };

      const response = await api.put<
        UpdateCampaignEmailResponse | CampaignErrorResponse
      >(`/campaigns/${campaignId}/emails/${email.id}`, requestBody);

      if (response.data.success) {
        onSave();
      } else {
        setError(response.data.error);
      }
    } catch (err) {
      console.error('Error saving email:', err);
      setError(
        err instanceof Error ? err.message : 'Failed to save email changes',
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className='campaign-edit-messaging-email'>
      <div className='edit-header'>
        <h3>Edit Email</h3>
        <div className='header-controls'>
          <div className='checkbox-group'>
            <input
              type='checkbox'
              id='send-new-thread'
              checked={sendAsNewThread}
              onChange={(e) => setSendAsNewThread(e.target.checked)}
            />
            <label htmlFor='send-new-thread'>Send as new thread</label>
          </div>
          <div className='send-delay-group'>
            <label htmlFor='send-delay'>Send delay</label>
            <input
              type='number'
              id='send-delay'
              value={sendDelay}
              onChange={(e) => setSendDelay(parseInt(e.target.value))}
              min='0'
              disabled={isFirstEmail}
            />
            <span>days</span>
          </div>
        </div>
      </div>

      {sendAsNewThread && (
        <div className='form-group subject-group'>
          <label htmlFor='subject'>Subject</label>
          <input
            type='text'
            id='subject'
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
        </div>
      )}

      <div className='edit-form'>
        <div className='form-group'>
          <label htmlFor='message'>Message</label>
          <div className='message-help-text'>
            Customise your emails with variables. You can add{' '}
            <span>{'{{sender_first_name}}'}</span>,{' '}
            <span>{'{{sender_last_name}}'}</span>,{' '}
            <span>{'{{receiver_first_name}}'}</span>,{' '}
            <span>{'{{receiver_last_name}}'}</span> and{' '}
            <span>{'{{receiver_company_name}}'}</span>.
          </div>
          <WysiwygEditor
            value={message}
            onChange={setMessage}
            placeholder='Email body...'
            className='campaign-email-wysiwyg'
            questionPaste={true}
          />
        </div>
      </div>

      {error && <ErrorState title='Failed to save email' subtitle={error} />}

      <div className='email-actions'>
        <span>Email actions</span>
        <div className='action-buttons'>
          <button type='button' onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save'}
          </button>
          <button type='button' onClick={onCancel} disabled={isSaving}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default CampaignEditMessagingEmail;
